import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  FiChevronUp, 
  FiChevronDown, 
  FiX, 
  FiDownload, 
  FiCheck, 
  FiAlertCircle,
  FiMinimize2,
  FiMaximize2
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  selectAllExports,
  selectIsExportPanelOpen,
  selectTotalActiveExports,
  selectActiveExports,
  selectCompletedExports,
  closeExportPanel,
  toggleExportPanel,
  clearCompletedExports,
  EXPORT_STATUS
} from '../../store/slices/export.slice';
import ExportTaskItem, { CompactExportTaskItem } from './ExportTaskItem';
import toast from 'react-hot-toast';

/**
 * ExportPanel Component
 * Google Drive-like export panel that appears in bottom-right corner
 * Shows progress for all export tasks with ability to minimize/maximize
 */
const ExportPanel = ({ 
  position = 'bottom-right', // 'bottom-right', 'bottom-left', 'top-right', 'top-left'
  className = ''
}) => {
  const dispatch = useDispatch();
  const exports = useSelector(selectAllExports);
  const isOpen = useSelector(selectIsExportPanelOpen);
  const totalActiveExports = useSelector(selectTotalActiveExports);
  const activeExports = useSelector(selectActiveExports);
  const completedExports = useSelector(selectCompletedExports);

  const [isMinimized, setIsMinimized] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);

  const exportsArray = Object.values(exports);
  const hasExports = exportsArray.length > 0;
 
  // Get position classes
  const getPositionClasses = () => {
    const baseClasses = 'fixed z-50';
    
    switch (position) {
      case 'bottom-left':
        return `${baseClasses} bottom-4 left-4`;
      case 'top-right':
        return `${baseClasses} top-4 right-4`;
      case 'top-left':
        return `${baseClasses} top-4 left-4`;
      default: // bottom-right
        return `${baseClasses} bottom-4 right-4`;
    }
  };

  // Get summary text
  const getSummaryText = () => {
    const total = exportsArray.length;
    const completed = completedExports.length;
    const active = activeExports.length;
    const failed = exportsArray.filter(e => e.status === EXPORT_STATUS.FAILED).length;

    if (active > 0) {
      return `${active} exporting, ${completed} ready`;
    } else if (failed > 0) {
      return `${completed} ready, ${failed} failed`;
    } else if (completed > 0) {
      return `${completed} ready to download`;
    }
    
    return `${total} exports`;
  };

  // Handle panel actions
  const handleClose = () => {
    if (totalActiveExports > 0) {
      // Allow closing but show info message
      toast.success(`Panel closed. ${totalActiveExports} export(s) will continue in background.`);
    }
    dispatch(closeExportPanel());
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleClearCompleted = () => {
    dispatch(clearCompletedExports());
    // If all exports are completed, close the panel
    if (exportsArray.length === completedExports.length) {
      dispatch(closeExportPanel());
    }
  };

  const handleClosePanel = () => {
    // Always close the panel, regardless of export status
    dispatch(closeExportPanel());
  };

  // Don't render if panel is closed
  if (!isOpen) {
    return null;
  }

  // If panel is open but no exports, show empty state with close button
  if (!hasExports) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.9 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={`${getPositionClasses()} ${className}`}
        >
          <div className="bg-white border border-gray-200 rounded-lg shadow-xl max-w-md w-80">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <div className="flex items-center gap-2">
                <FiDownload className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-gray-900">Exports</span>
              </div>
              
              <button
                onClick={handleClosePanel}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title="Close export panel"
              >
                <FiX className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Empty state */}
            <div className="p-6 text-center text-gray-500">
              <FiDownload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">No exports yet</p>
              <p className="text-xs mt-1">Start an export to see it here</p>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Show mini indicator when panel is closed but exports are active
  if (!isOpen && totalActiveExports > 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className={`${getPositionClasses()} ${className}`}
      >
        <div className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg cursor-pointer hover:bg-blue-700 transition-colors">
          <div className="flex items-center gap-2">
            <FiDownload className="w-4 h-4" />
            <span className="text-sm font-medium">
              {totalActiveExports} export(s) in progress
            </span>
            <button
              onClick={() => dispatch(toggleExportPanel())}
              className="ml-2 p-1 hover:bg-blue-800 rounded"
              title="Show export panel"
            >
              <FiChevronUp className="w-3 h-3" />
            </button>
            <button
              onClick={() => dispatch(closeExportPanel())}
              className="ml-2 p-1 hover:bg-blue-800 rounded"
              title="Close export panel"
            >
              <FiX className="w-3 h-3" />
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.9 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={`${getPositionClasses()} ${className}`}
        >
          <div className="bg-white border border-gray-200 rounded-lg shadow-xl max-w-md w-80">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <div className="flex items-center gap-2">
                <FiDownload className="w-4 h-4 text-blue-600" />
                <div>
                  <span className="font-medium text-gray-900">
                    {totalActiveExports > 0 ? 'Exports' : 'Exports'}
                  </span>
                  {totalActiveExports > 0 && (
                    <div className="text-xs text-gray-500">
                      ({totalActiveExports} active) - You can close this panel anytime
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                {/* Compact view toggle - only show when NOT minimized */}
                {!isMinimized && (
                  <button
                    onClick={() => setIsCompactView(!isCompactView)}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                    title={isCompactView ? 'Detailed view' : 'Compact view'}
                  >
                    {isCompactView ? (
                      <FiMaximize2 className="w-4 h-4 text-gray-600" />
                    ) : (
                      <FiMinimize2 className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                )}

                {/* Minimize/Maximize */}
                <button
                  onClick={handleMinimize}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title={isMinimized ? 'Expand panel to show full content' : 'Minimize panel to show summary only'}
                >
                  {isMinimized ? (
                    <FiChevronUp className="w-4 h-4 text-gray-600" />
                  ) : (
                    <FiChevronDown className="w-4 h-4 text-gray-600" />
                  )}
                </button>

                {/* Close button - always visible */}
                <button
                  onClick={handleClosePanel}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title="Close export panel"
                >
                  <FiX className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Summary bar (when minimized) */}
            <AnimatePresence>
              {isMinimized && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-3 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">{getSummaryText()}</span>
                      <div className="flex items-center gap-1">
                        {completedExports.length > 0 && (
                          <FiCheck className="w-4 h-4 text-green-500" />
                        )}
                        {activeExports.length > 0 && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        )}
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full bg-blue-500 transition-all duration-300"
                        style={{ 
                          width: `${exportsArray.length > 0 ? (completedExports.length / exportsArray.length) * 100 : 0}%` 
                        }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Content */}
            <AnimatePresence>
              {!isMinimized && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  {/* Action buttons */}
                  {exportsArray.length > 0 && (
                    <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-50">
                      <span className="text-sm text-gray-600">
                        {getSummaryText()}
                      </span>
                      
                      {completedExports.length > 0 && (
                        <button
                          onClick={handleClearCompleted}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline"
                        >
                          Clear completed
                        </button>
                      )}
                    </div>
                  )}

                  {/* Export list */}
                  <div className="max-h-96 overflow-y-auto">
                    {exportsArray.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <FiDownload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">No exports yet</p>
                      </div>
                    ) : (
                      <div className={`p-3 space-y-3`}>
                        {exportsArray.map(exportTask => (
                          <motion.div
                            key={exportTask.export_id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                          >
                            {isCompactView ? (
                              <CompactExportTaskItem 
                                exportTask={exportTask}
                                showControls={true}
                              />
                            ) : (
                              <ExportTaskItem 
                                exportTask={exportTask}
                                showControls={true}
                              />
                            )}
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer with overall progress */}
                  {exportsArray.length > 1 && (
                    <div className="p-3 border-t border-gray-200 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Overall Progress
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {exportsArray.length > 0 ? Math.round((completedExports.length / exportsArray.length) * 100) : 0}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full bg-blue-500 transition-all duration-300"
                          style={{ 
                            width: `${exportsArray.length > 0 ? (completedExports.length / exportsArray.length) * 100 : 0}%` 
                          }}
                        />
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Mini Export Panel - Compact version that can be embedded anywhere
 */
export const MiniExportPanel = ({ className = '' }) => {
  const exports = useSelector(selectAllExports);
  const totalActiveExports = useSelector(selectTotalActiveExports);
  const dispatch = useDispatch();

  const exportsArray = Object.values(exports);
  const hasActiveExports = totalActiveExports > 0;

  if (!hasActiveExports) return null;

  const handleTogglePanel = () => {
    dispatch(toggleExportPanel());
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg cursor-pointer hover:bg-blue-700 transition-colors ${className}`}
      onClick={handleTogglePanel}
    >
      <div className="flex items-center gap-2">
        <FiDownload className="w-4 h-4" />
        <span className="text-sm font-medium">
          {totalActiveExports} exporting
        </span>
      </div>
    </motion.div>
  );
};

/**
 * Export Status Badge - Shows export count in header/navbar
 */
export const ExportStatusBadge = ({ className = '' }) => {
  const totalActiveExports = useSelector(selectTotalActiveExports);
  const dispatch = useDispatch();

  if (totalActiveExports === 0) return null;

  const handleClick = () => {
    dispatch(toggleExportPanel());
  };

  return (
    <button
      onClick={handleClick}
      className={`relative inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-full hover:bg-blue-700 transition-colors ${className}`}
    >
      <FiDownload className="w-4 h-4 mr-1" />
      <span>{totalActiveExports}</span>
      
      {/* Animated pulse indicator */}
      <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-green-400 animate-pulse" />
    </button>
  );
};

export default ExportPanel;
